/*
 * Collection cards: wide custom card images (desktop, custom cards spanning 2+ columns).
 *
 * The image box spans the card's full inner width at the stock image height (CSS). This script
 * measures where the band sits in each photo (non-background rows/columns) and scales the photo up
 * to fill the box width, cropping only the white space above/below the band. If filling the width
 * would cut into the band, the photo is scaled as large as it can go without cutting, centered.
 * The crop is centered vertically, clamped so the band always stays inside the box (so a band that
 * touches the photo's bottom edge is cropped from the top only).
 *
 * Output: --cc-img-w (width as % of the box) and --cc-img-top (% of box height) on the <img>, plus
 * .is-fitted. If the photo can't be read (e.g. CORS) nothing is set and the CSS fallback stays.
 */
(function () {
  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  // --wide is set in the section for custom cards spanning 2+ desktop columns (incl. stretched)
  const IMG_SELECTOR = '.collection-cards__item--custom.collection-cards__item--wide .imgpackcon img.desktop-image-collection-card';
  const SAMPLE_WIDTH = 200;
  const bands = new WeakMap();

  // Band bounds as fractions of the photo: { top, bottom } (bottom = exclusive end)
  function measureBand(img) {
    if (bands.has(img)) return bands.get(img);
    let band = null;
    try {
      const w = SAMPLE_WIDTH;
      const h = Math.max(1, Math.round((SAMPLE_WIDTH * img.naturalHeight) / img.naturalWidth));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const px = (x, y) => (y * w + x) * 4;

      // Background = the most common corner color
      const counts = {};
      [px(0, 0), px(w - 1, 0), px(0, h - 1), px(w - 1, h - 1)].forEach((i) => {
        const key = data.slice(i, i + 4).join(',');
        counts[key] = (counts[key] || 0) + 1;
      });
      const bgKey = Object.keys(counts).reduce((a, b) => (counts[b] > counts[a] ? b : a));
      const bg = bgKey.split(',').map(Number);

      const isBand = (i) => {
        if (data[i + 3] < 16) return false;
        if (bg[3] >= 16 && Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) < 36) return false;
        return true;
      };

      let top = -1;
      let bottom = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (isBand(px(x, y))) {
            if (top < 0) top = y;
            bottom = y;
            break;
          }
        }
      }
      if (top >= 0) band = { top: top / h, bottom: (bottom + 1) / h };
    } catch (e) {
      band = null; // unreadable (cross-origin) or not decoded: keep the CSS fallback
    }
    bands.set(img, band);
    return band;
  }

  function reset(img) {
    img.classList.remove('is-fitted');
    img.style.removeProperty('--cc-img-w');
    img.style.removeProperty('--cc-img-top');
  }

  function fit(img) {
    const box = img.closest('.imgpackcon');
    if (!box || !DESKTOP.matches || !img.naturalWidth) return reset(img);

    const band = measureBand(img);
    if (!band) return reset(img);

    const W = box.clientWidth;
    const H = box.clientHeight;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (!W || !H) return;

    const bandHeight = (band.bottom - band.top) * ih;
    const scale = Math.min(Math.max(W / iw, H / ih), H / bandHeight);
    const renderedW = scale * iw;
    const crop = Math.max(0, scale * ih - H);

    // Centered crop, clamped so the band stays fully inside the box
    const minTop = scale * band.bottom * ih - H;
    const maxTop = scale * band.top * ih;
    const cropTop = Math.min(Math.max(crop / 2, minTop), maxTop);

    img.style.setProperty('--cc-img-w', `${(renderedW / W) * 100}%`);
    img.style.setProperty('--cc-img-top', `${(-cropTop / H) * 100}%`);
    img.classList.add('is-fitted');
  }

  function init() {
    const imgs = document.querySelectorAll(IMG_SELECTOR);
    if (!imgs.length) return;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const img = entry.target.querySelector('img.desktop-image-collection-card');
        if (img) fit(img);
      });
    });

    imgs.forEach((img) => {
      if (img.complete) fit(img);
      else img.addEventListener('load', () => fit(img), { once: true });
      resizeObserver.observe(img.closest('.imgpackcon'));
    });

    DESKTOP.addEventListener('change', () => imgs.forEach(fit));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
